/**
 * InfoPopup — lightweight info dialog for natal-chart elements.
 *
 * Tap-driven explainer that appears when the user taps a sign, planet, house
 * or angle in the chart or the tables. Closes on backdrop tap, Esc or the
 * close button. Title + optional subtitle + 1–3 sentence body.
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './InfoPopup.module.css';

export interface InfoPopupProps {
  open: boolean;
  title: string;
  subtitle?: string;
  body: string;
  glyph?: string;
  onClose: () => void;
  'data-testid'?: string;
}

export function InfoPopup({
  open, title, subtitle, body, glyph, onClose, 'data-testid': testId,
}: InfoPopupProps): JSX.Element | null {
  const { t } = useTranslation('common');
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="info-popup-title"
      data-testid={testId ?? 'natal-info-popup'}
      onClick={onClose}
    >
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
        {glyph && <div className={styles.glyph} aria-hidden="true">{glyph}</div>}
        <h2 id="info-popup-title" className={styles.title}>{title}</h2>
        {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
        <p className={styles.body}>{body}</p>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          data-testid="natal-info-close"
        >
          {t('actions.close')}
        </button>
      </div>
    </div>
  );
}
